
import Vue from 'vue';
import { ChatMessage } from '@/model';
import api from '@/rest-api';
import ChatMarkdown from '@/components/ChatMarkdown.vue';

const SUGGESTIONS = [
  'What documents are available?',
  'What labels exist and what do they mean?',
  'Search for fragments about bribery',
  'What transaction patterns are documented?',
];

export default Vue.extend({
  name: 'AssistantView',
  components: { ChatMarkdown },

  data() {
    return {
      history: [] as ChatMessage[],
      loading: false,
      prompt: '',
      suggestions: SUGGESTIONS,
      thinkingText: '' as string,
      thinkingOpen: false as boolean,
      tokenUsage: null as { model: string; inputTokens: number; outputTokens: number } | null,
    };
  },

  computed: {
    isStreaming(): boolean {
      return this.loading && this.history.length > 0 && this.history[this.history.length - 1].role === 'assistant';
    },

    estimatedCost(): string | null {
      if (!this.tokenUsage) return null;
      const { model, inputTokens, outputTokens } = this.tokenUsage;
      const m = model.toLowerCase();
      let inputPrice: number;
      let outputPrice: number;
      if (m.includes('haiku')) {
        inputPrice = 1.00; outputPrice = 5.00;
      } else if (m.includes('opus')) {
        inputPrice = 5.00; outputPrice = 25.00;
      } else {
        // sonnet default
        inputPrice = 3.00; outputPrice = 15.00;
      }
      const cost = (inputTokens / 1_000_000) * inputPrice + (outputTokens / 1_000_000) * outputPrice;
      return cost < 0.0001 ? '<$0.0001' : `~$${cost.toFixed(4)}`;
    },
  },

  watch: {
    history() {
      this.$nextTick(() => {
        const el = this.$refs.messages as HTMLElement | undefined;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
  },

  methods: {
    isStreamingMessage(index: number): boolean {
      return this.isStreaming && index === this.history.length - 1;
    },

    isStatusContent(content: string): boolean {
      return !!(content && content.length < 80 && !content.includes('\n'));
    },

    reset() {
      this.history = [];
      this.prompt = '';
      this.thinkingText = '';
      this.thinkingOpen = false;
      this.tokenUsage = null;
    },

    async send(text: string) {
      if (!text.trim() || this.loading) return;
      this.prompt = '';

      const userMsg: ChatMessage = { role: 'user', content: text };
      const historyWithUser = [...this.history, userMsg];
      this.history = [...historyWithUser, { role: 'assistant', content: '' }];
      this.loading = true;
      this.thinkingText = '';
      this.thinkingOpen = false;
      let hasTokens = false;

      try {
        await api.assistantChatStream(
          historyWithUser,
          (token: string) => {
            const lastIndex = this.history.length - 1;
            if (!hasTokens) {
              hasTokens = true;
              this.history = [...this.history.slice(0, lastIndex), { ...this.history[lastIndex], content: '' }];
            }
            const updated: ChatMessage = { ...this.history[lastIndex], content: this.history[lastIndex].content + token };
            this.history = [...this.history.slice(0, lastIndex), updated];
          },
          (status: string) => {
            if (status.startsWith('thinking:')) {
              this.thinkingText += status.slice(9);
            } else if (status.startsWith('usage:')) {
              try {
                const u = JSON.parse(status.slice(6));
                this.tokenUsage = {
                  model: u.model,
                  inputTokens: (this.tokenUsage?.inputTokens ?? 0) + u.inputTokens,
                  outputTokens: (this.tokenUsage?.outputTokens ?? 0) + u.outputTokens,
                };
              } catch { /* ignore */ }
            } else if (!hasTokens) {
              const lastIndex = this.history.length - 1;
              this.history = [...this.history.slice(0, lastIndex), { ...this.history[lastIndex], content: status }];
            }
          },
        );
      } catch {
        const lastIndex = this.history.length - 1;
        this.history = [...this.history.slice(0, lastIndex), { role: 'assistant', content: 'Something went wrong. Please try again.' }];
      } finally {
        this.loading = false;
      }
    },
  },
});
